import React from 'react';
import { SingleSKU } from '../overview/SKU/SingleSKU';
import { ProductCardFooter } from './ProductCardFooter';
import { ProductCardType, type IProductCard } from './ProductCard.types';
import { ProductCardFavoriteRecipe } from './ProductCardFavoriteRecipe';
import { useI18N } from "../../../my-puratos/hoc/withI18N";
import { pdpGA4TrackSelectItem } from '../../../../analytics/ga4/events/gaEventsPDP';
import { setProductCrossSell } from '../../../../actions/analytics';
import { mapComponentTypeToCrossSellType } from '../../../../analytics/ga4/types/component';
import { useDispatch } from 'react-redux';
import { ProductCardRecipeTags } from './ProductCardRecipeTags';
import { ProductCardTags } from './ProductCardTags';

const productCardTypeClassname = (type: ProductCardType) => {
    switch (type) {
        case ProductCardType.PRODUCT:
            return "type--product";
        case ProductCardType.RECIPE:
            return "type--recipe";
        default:
            return "type--unknown"
    }
};

const getActivePromotionCode = (product) => {
    return product.Packagings?.[0]?.PromoCode;
}

const getActiveMaterialNumber = (product) => {
    return product.Packagings?.[0]?.MaterialNumber;
}

export const ProductCard = ({ data }) => {
    const {
        type,
        image,
        hoverImage,
        tags,
        title,
        subtitle,
        text,
        link,
        product,
        hasProductPrice,
        hasProductFooter,
        hasRecipeFooter,
        isFavorite,
        componentType
    } = data as IProductCard;
    const translate = useI18N();

    const isRecipeType = type === ProductCardType.RECIPE;
    const activePromotionCode = getActivePromotionCode(product);
    const hasHoverImage = hoverImage?.src && (hoverImage?.src !== image?.src);

    const dispatch = useDispatch();
    const linkClickHandler = (product, type: ProductCardType, componentType) => {
        if (type === ProductCardType.PRODUCT) {
            pdpGA4TrackSelectItem(product, componentType);
            dispatch(
                  setProductCrossSell({ 
                    ...product, 
                    materialNumber: getActiveMaterialNumber(product), 
                    type: mapComponentTypeToCrossSellType(componentType)
                })
            );
        }
    }

    return (
        <div className={`product-card ${productCardTypeClassname(type)}`}>
            <div className="product-card__header">
                {isRecipeType && (
                    <ProductCardFavoriteRecipe recipe={product} favoriteRecipe={isFavorite}/>
                )}
                { activePromotionCode && (
                    <div className="pdp__badges-container">
                        <span className="pdp-badge pdp-badge--promo">
                            {activePromotionCode}
                        </span> 
                    </div>
                )}
                {image?.src && (
                    <a className={`product-card-img--link ${hasHoverImage && "has-hover-img"}`} 
                        href={link} 
                        onClick={() => linkClickHandler(product, type, componentType)}
                        onMouseDown={(e) => e.button === 1 && linkClickHandler(product, type, componentType)}>
                        <img className="product-card-img" src={image.src} alt={image.alt}></img>
                        {hasHoverImage && (
                            <img className="product-card-img hover-img" src={hoverImage.src} alt={hoverImage.alt}></img>
                        )}
                    </a>
                )}
            </div>
            <div className="product-card__content">
                {isRecipeType && (
                    <ProductCardRecipeTags badges={tags} />
                )}
                {!isRecipeType && (
                    <ProductCardTags badges={tags} />
                )}

                {title && (
                    <a className="product-card__content-title--link" href={link} 
                        onClick={() => linkClickHandler(product, type, componentType)}
                        onMouseDown={(e) => e.button === 1 && linkClickHandler(product, type, componentType)}>
                        <h3 className="product-card__content-title">{title}</h3>
                    </a>
                )}
                {subtitle && (
                    <h4 className="product-card__content-subtitle">{subtitle}</h4>
                )}
                {text && <div className="product-card__content-text">{text}</div>}
            </div>
            <div className={`product-card__footer`}>
                {hasProductPrice && (
                    <div className="product-card__content-prices">
                        <SingleSKU enabled={true} outsideContext={product} />
                    </div>
                )}
                {hasProductFooter && (
                    <ProductCardFooter product={product} componentType={componentType}/>
                )}
                {(hasRecipeFooter && link) &&  (
                        <a href={link} className="product-card__link">{translate('View recipe')}</a>
                )}
            </div>
        </div>
    );
};
